import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './state/AuthContext.jsx';
import { Icon, Mark } from './components/Icons.jsx';
import { initials } from './ui.jsx';

import Home from './pages/Home.jsx';
import SignIn from './pages/auth/SignIn.jsx';
import SignUp from './pages/auth/SignUp.jsx';
import VerifyEmail from './pages/auth/VerifyEmail.jsx';
import ForgotPassword from './pages/auth/ForgotPassword.jsx';
import ResetPassword from './pages/auth/ResetPassword.jsx';
import Schools from './pages/public/Schools.jsx';
import SchoolDetail from './pages/public/SchoolDetail.jsx';
import Forums from './pages/public/Forums.jsx';
import ForumThread from './pages/public/ForumThread.jsx';
import Match from './pages/student/Match.jsx';
import Profile from './pages/student/Profile.jsx';
import Apply from './pages/student/Apply.jsx';
import Track from './pages/student/Track.jsx';
import Advisor from './pages/student/Advisor.jsx';
import Notifications from './pages/Notifications.jsx';
import Account from './pages/Account.jsx';
import AdminDashboard from './pages/admin/Dashboard.jsx';
import AdminApplications from './pages/admin/Applications.jsx';
import AdminApplicationDetail from './pages/admin/ApplicationDetail.jsx';
import AdminDates from './pages/admin/Dates.jsx';
import AdminProgrammes from './pages/admin/Programmes.jsx';
import AdminOffers from './pages/admin/Offers.jsx';
import AdminBroadcast from './pages/admin/Broadcast.jsx';
import AdminActivity from './pages/admin/Activity.jsx';
import AdminStaff from './pages/admin/Staff.jsx';

/** Navigation groups, matching the prototype's sidebar. */
const studentNav = [
  ['Explore', [['/', 'Home'], ['/match', 'Match'], ['/advisor', 'AI Advisor'], ['/schools', 'Colleges']]],
  ['Apply', [['/apply', 'Applications'], ['/track', 'Track']]],
  ['Community', [['/forums', 'Forums']]],
  // /profile is the master bio (about you, education, programme choices, the four
  // documents). It used to be reachable only from the post-verification redirect
  // and a link inside Apply, so once you left that page there was no way back to
  // it from the sidebar. /account below is login and security, a different thing.
  ['Account', [['/profile', 'My profile'], ['/account', 'Your details']]]
];

const staffNav = [['', [['/', 'Home'], ['/admin', 'Admissions'], ['/schools', 'Colleges'], ['/forums', 'Forums'], ['/account', 'Your details']]]];

/**
 * A platform admin is not school staff and has no candidate pool of their own,
 * so they get neither the applicant nav nor the admissions nav. Their job here
 * is provisioning: without a staff account a school cannot get into its own
 * dashboard to publish dates or answer applicants.
 */
const platformNav = [
  ['', [['/', 'Home'], ['/admin/staff', 'Staff accounts'], ['/schools', 'Colleges'], ['/forums', 'Forums']]],
  ['Account', [['/account', 'Your details']]]
];

const navFor = (isSchoolStaff, isPlatformAdmin) =>
  isPlatformAdmin ? platformNav : isSchoolStaff ? staffNav : studentNav;

/** The line under the name in the sidebar footer. */
const roleLabel = (isSchoolStaff, isPlatformAdmin) =>
  isPlatformAdmin ? 'Platform admin' : isSchoolStaff ? 'College staff' : 'Applicant';

const iconFor = (to) => {
  if (to === '/') return 'home';
  if (to.startsWith('/match')) return 'match';
  if (to.startsWith('/advisor')) return 'advisor';
  if (to.startsWith('/schools')) return 'schools';
  if (to.startsWith('/apply')) return 'apply';
  if (to.startsWith('/track')) return 'track';
  if (to.startsWith('/forums')) return 'forums';
  if (to.startsWith('/account')) return 'account';
  return 'admin';
};

/** Blocks a route until the session is known, so a refresh does not bounce. */
const Guard = ({ children, staffOnly, adminOnly }) => {
  const { user, ready, isSchoolStaff, isPlatformAdmin } = useAuth();
  const location = useLocation();

  if (!ready) return <div className="note">Checking your session...</div>;
  if (!user) return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  if (staffOnly && !isSchoolStaff) return <Navigate to="/" replace />;
  if (adminOnly && !isPlatformAdmin) return <Navigate to="/" replace />;
  return children;
};

function Sidebar() {
  const { user, signOut, isSchoolStaff, isPlatformAdmin } = useAuth();
  const groups = navFor(isSchoolStaff, isPlatformAdmin);

  return (
    <aside className="side">
      <Link to="/" className="logo" style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 0, cursor: 'pointer', color: 'inherit' }}>
        <Mark /> Entrance
      </Link>
      {groups.map(([group, items]) => (
        <div key={group || 'root'}>
          {group ? <div className="grp">{group}</div> : null}
          {items.map(([to, label]) => (
            <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `item ${isActive ? 'on' : ''}`}>
              <Icon name={iconFor(to)} /> {label}
            </NavLink>
          ))}
        </div>
      ))}
      <div className="foot">
        {user ? (
          <>
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <span className="av" style={{ margin: 0 }}>{initials(user.name)}</span>
              <div style={{ minWidth: 0 }}>
                <b style={{ fontSize: 13 }}>{user.name}</b>
                <div className="mu">{roleLabel(isSchoolStaff, isPlatformAdmin)}</div>
              </div>
            </div>
            <button type="button" className="btn s sm" onClick={signOut}>Log out</button>
          </>
        ) : (
          <>
            <Link to="/signup" className="btn sm" style={{ textDecoration: 'none' }}>Sign up</Link>
            <Link to="/signin" className="btn s sm" style={{ textDecoration: 'none' }}>Log in</Link>
          </>
        )}
      </div>
    </aside>
  );
}

function Header() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { isSchoolStaff, isPlatformAdmin } = useAuth();
  const location = useLocation();

  const groups = navFor(isSchoolStaff, isPlatformAdmin);
  const items = groups.flatMap(([, list]) => list);

  // The mobile menu closes on navigation, otherwise it stays over the page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header>
      {/* No inline style here. `display:flex` was set inline, and an inline
          style outranks any stylesheet rule, so the desktop rule that hides the
          wordmark could never take effect and the logo printed twice. */}
      <Link to="/" className="logo">
        <Mark /> Entrance
      </Link>
      <nav className={open ? 'open' : ''} aria-label="Main">
        {items.map(([to, label]) => (
          <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => (isActive ? 'on' : '')}>
            {label}
          </NavLink>
        ))}
      </nav>
      {user ? (
        <Link to="/notifications" className="av" aria-label="Notifications" style={{ background: 'none', border: 0, cursor: 'pointer' }}>
          {initials(user.name)}
        </Link>
      ) : (
        <div className="hauth">
          <Link to="/signin" className="btn s sm" style={{ textDecoration: 'none' }}>Log in</Link>
          <Link to="/signup" className="btn sm" style={{ textDecoration: 'none' }}>Sign up</Link>
        </div>
      )}
      <button
        type="button"
        className="burger"
        aria-label="Menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
        </svg>
      </button>
    </header>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  // Braces, not a bare expression: whatever `scrollTo` returns would otherwise
  // be taken as the effect's clean-up function, and React throws
  // "destroy is not a function" when it tries to call it on unmount.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const { ready } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="shell">
      <Sidebar />
      <div className="col">
        <Header />
        <main className="w">
          <ScrollToTop />
          {ready ? (
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/schools" element={<Schools />} />
              <Route path="/schools/:slug" element={<SchoolDetail />} />
              <Route path="/forums" element={<Forums />} />
              <Route path="/forums/:id" element={<ForumThread />} />

              <Route path="/match" element={<Guard><Match /></Guard>} />
              <Route path="/profile" element={<Guard><Profile /></Guard>} />
              <Route path="/apply" element={<Guard><Apply /></Guard>} />
              <Route path="/track" element={<Guard><Track /></Guard>} />
              <Route path="/advisor" element={<Guard><Advisor /></Guard>} />
              <Route path="/notifications" element={<Guard><Notifications /></Guard>} />
              <Route path="/account" element={<Guard><Account /></Guard>} />

              <Route path="/admin" element={<Guard staffOnly><AdminDashboard /></Guard>} />
              <Route path="/admin/applications" element={<Guard staffOnly><AdminApplications /></Guard>} />
              <Route path="/admin/applications/:id" element={<Guard staffOnly><AdminApplicationDetail /></Guard>} />
              <Route path="/admin/dates" element={<Guard staffOnly><AdminDates /></Guard>} />
              <Route path="/admin/programmes" element={<Guard staffOnly><AdminProgrammes /></Guard>} />
              <Route path="/admin/offers" element={<Guard staffOnly><AdminOffers /></Guard>} />
              <Route path="/admin/broadcast" element={<Guard staffOnly><AdminBroadcast /></Guard>} />
              <Route path="/admin/activity" element={<Guard staffOnly><AdminActivity /></Guard>} />

              {/* Platform admin only, and deliberately outside the staffOnly group:
                  a school admin is scoped to one school's applicants and has no
                  business provisioning accounts. */}
              <Route path="/admin/staff" element={<Guard adminOnly><AdminStaff /></Guard>} />

              <Route path="*" element={<NotFound onHome={() => navigate('/')} />} />
            </Routes>
          ) : null}
        </main>
        <footer>
          Requirements come from public training-college notices. Cut-offs marked as estimates change
          every year, so always confirm with the college before you pay.
        </footer>
      </div>
    </div>
  );
}

const NotFound = ({ onHome }) => (
  <div className="card">
    <h2>Page not found</h2>
    <p className="mu">That page does not exist.</p>
    <button type="button" className="btn" onClick={onHome}>Go to home</button>
  </div>
);
